import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Search, ShoppingBag, Menu, X, Globe, ChevronDown } from "lucide-react";
import { t } from "@/i18n/sk";
import { useCart } from "@/context/CartContext";
import { Logo } from "@/components/Logo";

const links = [
  { to: "/", label: t.nav.home, testId: "nav-link-home", end: true },
  { to: "/serie", label: t.nav.series, testId: "nav-link-series" },
  { to: "/etb-boxy", label: t.nav.etb, testId: "nav-link-etb" },
  { to: "/boostre", label: t.nav.boosters, testId: "nav-link-boosters" },
  { to: "/karty", label: t.nav.cards, testId: "nav-link-cards" },
  { to: "/kontakt", label: t.nav.contact, testId: "nav-link-contact" },
];

function LanguageSelector() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        data-testid="language-selector"
        className="flex items-center gap-1.5 rounded-full border border-white/8 px-3 py-2 text-xs font-semibold text-slate-200 transition-colors hover:border-cyan-glow/40 hover:text-white"
      >
        <Globe className="h-4 w-4" />
        SK · €
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-44 rounded-xl border border-white/10 glass p-1.5 shadow-xl">
          <div className="rounded-lg bg-violet-glow/15 px-3 py-2 text-sm font-semibold text-white">Slovenčina · EUR</div>
        </div>
      )}
    </div>
  );
}

export function Navbar({ onOpenSearch }) {
  const { count, setOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-white/8 glass">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-7">
          <Logo />
          <ul className="hidden items-center gap-0.5 lg:flex">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.end}
                  data-testid={l.testId}
                  className={({ isActive }) =>
                    `relative rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${isActive ? "text-white" : "text-slate-300 hover:text-white"}`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {l.label}
                      {isActive && <span className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-gradient-to-r from-violet-glow to-cyan-glow" />}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={onOpenSearch} data-testid="search-trigger" aria-label={t.actions.search} className="flex h-10 w-10 items-center justify-center rounded-full border border-white/8 text-slate-200 transition-colors hover:border-cyan-glow/40 hover:text-white">
            <Search className="h-4 w-4" />
          </button>
          <div className="hidden sm:block"><LanguageSelector /></div>
          <button onClick={() => setOpen(true)} data-testid="cart-drawer-trigger-button" aria-label={t.actions.cart} className="relative flex h-10 items-center gap-2 rounded-full border border-white/8 px-4 text-slate-100 transition-colors hover:border-violet-glow/50 hover:text-white">
            <ShoppingBag className="h-4 w-4" />
            {count > 0 && <span data-testid="cart-count" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-gradient-to-br from-violet-glow to-cyan-glow px-1.5 text-xs font-bold text-white">{count}</span>}
          </button>
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-trigger" aria-label="Menu" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/8 text-slate-200 lg:hidden">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-vault-black/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 border-r border-white/10 bg-vault-midnight p-5 shadow-2xl">
            <div className="mb-8 flex items-center justify-between">
              <Logo />
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" className="text-slate-300"><X className="h-6 w-6" /></button>
            </div>
            <ul className="flex flex-col gap-1">
              {links.map((l) => (
                <li key={l.to}>
                  <NavLink to={l.to} end={l.end} onClick={() => setMobileOpen(false)} data-testid={`mobile-${l.testId}`} className={({ isActive }) => `block rounded-lg px-4 py-3 text-base font-medium ${isActive ? "bg-violet-glow/15 text-white" : "text-slate-300"}`}>
                    {l.label}
                  </NavLink>
                </li>
              ))}
            </ul>
            <div className="mt-6 border-t border-white/8 pt-6"><LanguageSelector /></div>
          </div>
        </div>
      )}
    </header>
  );
}

export default Navbar;
